import React from 'react';

const ViewCompanyModal = ({ company, isOpen, onClose }) => {
  if (!isOpen || !company) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-3xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto shadow-2xl border border-gray-200">
        {/* Modal Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white w-10 h-10 rounded-xl flex items-center justify-center font-bold text-base">
              {company?.name?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <h2 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {company.name}
              </h2>
              <p className="text-gray-600 text-xs">Complete Company Information • ID: {company._id?.slice(-6)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition-all duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <div className="grid grid-cols-1 gap-4">
          {/* Images Section */}
          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-3 rounded-xl border border-indigo-200">
            <div className="flex items-center space-x-2 mb-2">
              <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="text-sm font-bold text-gray-800">Company Images & Assets</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white p-3 rounded-lg flex flex-col items-center shadow-sm">
                <h4 className="font-semibold text-gray-700 mb-2 text-xs">Company Logo</h4>
                <div className="w-24 h-24 relative flex items-center justify-center bg-gray-50 rounded-md shadow-inner overflow-hidden">
                  <img 
                    src={`${process.env.REACT_APP_API_BASE_URL}/companies/image/${company._id}?${Date.now()}`} 
                    alt="Company Logo" 
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/150?text=No+Logo';
                    }}
                  />
                </div>
              </div>
              <div className="bg-white p-3 rounded-lg flex flex-col items-center shadow-sm">
                <h4 className="font-semibold text-gray-700 mb-2 text-xs">Bank QR Code</h4>
                <div className="w-24 h-24 relative flex items-center justify-center bg-gray-50 rounded-md shadow-inner overflow-hidden">
                  <img 
                    src={`${process.env.REACT_APP_API_BASE_URL}/companies/bankQR/${company._id}?${Date.now()}`} 
                    alt="Bank QR" 
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/150?text=No+QR+Code';
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-xl border border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <h3 className="text-sm font-bold text-gray-800">Company Information</h3>
              </div>
              <div className="flex space-x-1">
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${company.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {company.status === 'active' ? '✓ ACTIVE' : '✗ INACTIVE'}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${company.isStockStatus ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                  {company.isStockStatus ? 'STOCK' : 'NO STOCK'}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${company.isTallyMaestroEnabled ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                  {company.isTallyMaestroEnabled ? '✓ TALLY MAESTRO' : '✗ TALLY MAESTRO'}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-white p-2 rounded-md">
                <span className="font-semibold text-gray-600 block">Company Name</span>
                <span className="text-gray-900 font-semibold">{company.name}</span>
              </div>
              <div className="bg-white p-2 rounded-md">
                <span className="font-semibold text-gray-600 block">Print Name</span>
                <span className="text-gray-900">{company.printName || 'N/A'}</span>
              </div>
              <div className="bg-white p-2 rounded-md">
                <span className="font-semibold text-gray-600 block">Email Address</span>
                <span className="text-gray-900 break-all">{company.email}</span>
              </div>
              <div className="bg-white p-2 rounded-md">
                <span className="font-semibold text-gray-600 block">Phone Number</span>
                <span className="text-gray-900">{company.phone}</span>
              </div>
              <div className="bg-white p-2 rounded-md col-span-2">
                <span className="font-semibold text-gray-600 block">Business Address</span>
                <span className="text-gray-900">{company.address}</span>
              </div>
              <div className="bg-white p-2 rounded-md">
                <span className="font-semibold text-gray-600 block">GST Number</span>
                <span className="text-gray-900 font-mono">{company.gstNo || 'Not Registered'}</span>
              </div>
              <div className="bg-white p-2 rounded-md">
                <span className="font-semibold text-gray-600 block">Created Date</span>
                <span className="text-gray-900">{company.createdAt ? new Date(company.createdAt).toLocaleDateString() : 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Banking Details */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 p-3 rounded-xl border border-green-200">
            <div className="flex items-center space-x-2 mb-2">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-sm font-bold text-gray-800">Banking & Financial Details</h3>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-white p-2 rounded-md">
                <span className="font-semibold text-gray-600 block">Bank Name</span>
                <span className="text-gray-900 font-semibold">{company.bankName || 'Not Provided'}</span>
              </div>
              <div className="bg-white p-2 rounded-md">
                <span className="font-semibold text-gray-600 block">Branch Name</span>
                <span className="text-gray-900">{company.branchName || 'Not Provided'}</span>
              </div>
              <div className="bg-white p-2 rounded-md">
                <span className="font-semibold text-gray-600 block">Account Number</span>
                <span className="text-gray-900 font-mono">{company.accountNo || 'Not Provided'}</span>
              </div>
              <div className="bg-white p-2 rounded-md">
                <span className="font-semibold text-gray-600 block">IFSC Code</span>
                <span className="text-gray-900 font-mono">{company.ifsc || 'Not Provided'}</span>
              </div>
            </div>
          </div>

          {/* Social Media Links */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-3 rounded-xl border border-purple-200">
            <div className="flex items-center space-x-2 mb-2">
              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
              </svg>
              <h3 className="text-sm font-bold text-gray-800">Digital Presence & Social Media</h3>
            </div>
                          <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-white p-2 rounded-md">
                  <div className="flex items-center space-x-1 mb-1">
                    <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                    </svg>
                    <span className="text-xs font-semibold text-gray-700">Website</span>
                  </div>
                  {company.website ? (
                    <a href={company.website.startsWith('http') ? company.website : `https://${company.website}`} 
                       target="_blank" rel="noopener noreferrer"
                       className="text-xs text-blue-600 hover:underline block truncate">
                      {company.website}
                    </a>
                  ) : (
                    <span className="text-xs text-gray-500 italic">Not configured</span>
                  )}
                </div>
                
                <div className="bg-white p-2 rounded-md">
                  <div className="flex items-center space-x-1 mb-1">
                    <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    <span className="text-xs font-semibold text-gray-700">Facebook</span>
                  </div>
                  {company.facebook ? (
                    <a href={company.facebook} target="_blank" rel="noopener noreferrer"
                       className="text-xs text-blue-600 hover:underline block truncate">
                      Facebook Page
                    </a>
                  ) : (
                    <span className="text-xs text-gray-500 italic">Not configured</span>
                  )}
                </div>

                <div className="bg-white p-2 rounded-md">
                  <div className="flex items-center space-x-1 mb-1">
                    <svg className="w-3 h-3 text-pink-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987c6.62 0 11.987-5.367 11.987-11.987C24.014 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.348-1.051-2.348-2.348s1.051-2.348 2.348-2.348 2.348 1.051 2.348 2.348S9.746 16.988 8.449 16.988zM12.017 7.129c1.297 0 2.348 1.051 2.348 2.348s-1.051 2.348-2.348 2.348S9.669 10.774 9.669 9.477 10.72 7.129 12.017 7.129z"/>
                    </svg>
                    <span className="text-xs font-semibold text-gray-700">Instagram</span>
                  </div>
                  {company.instagram ? (
                    <a href={company.instagram} target="_blank" rel="noopener noreferrer"
                       className="text-xs text-pink-600 hover:underline block truncate">
                      Instagram Profile
                    </a>
                  ) : (
                    <span className="text-xs text-gray-500 italic">Not configured</span>
                  )}
                </div>

                <div className="bg-white p-2 rounded-md">
                  <div className="flex items-center space-x-1 mb-1">
                    <svg className="w-3 h-3 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                    </svg>
                    <span className="text-xs font-semibold text-gray-700">Twitter</span>
                  </div>
                  {company.twitter ? (
                    <a href={company.twitter} target="_blank" rel="noopener noreferrer"
                       className="text-xs text-blue-400 hover:underline block truncate">
                      Twitter Profile
                    </a>
                  ) : (
                    <span className="text-xs text-gray-500 italic">Not configured</span>
                  )}
                </div>

                <div className="bg-white p-2 rounded-md">
                  <div className="flex items-center space-x-1 mb-1">
                    <svg className="w-3 h-3 text-blue-700" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    <span className="text-xs font-semibold text-gray-700">LinkedIn</span>
                  </div>
                  {company.linkedin ? (
                    <a href={company.linkedin} target="_blank" rel="noopener noreferrer"
                       className="text-xs text-blue-700 hover:underline block truncate">
                      LinkedIn Profile
                    </a>
                  ) : (
                    <span className="text-xs text-gray-500 italic">Not configured</span>
                  )}
                </div>

                <div className="bg-white p-2 rounded-md">
                  <div className="flex items-center space-x-1 mb-1">
                    <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                    <span className="text-xs font-semibold text-gray-700">YouTube</span>
                  </div>
                  {company.youtube ? (
                    <a href={company.youtube} target="_blank" rel="noopener noreferrer"
                       className="text-xs text-red-600 hover:underline block truncate">
                      YouTube Channel
                    </a>
                  ) : (
                    <span className="text-xs text-gray-500 italic">Not configured</span>
                  )}
                </div>
            </div>
          </div>

          {/* Tally Configurations */}
          <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 p-3 rounded-xl border border-cyan-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
                <h3 className="text-sm font-bold text-gray-800">Tally Configurations</h3>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${company.isTallyActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {company.isTallyActive ? '✓ ACTIVE' : '✗ INACTIVE'}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-2 text-xs">
              <div className="bg-white p-2 rounded-md">
                <span className="font-semibold text-gray-600 block">Tally Company Name</span>
                <span className="text-gray-900 font-semibold">{company.TallyCompany || 'Not Configured'}</span>
              </div>
              <div className="bg-white p-2 rounded-md">
                <span className="font-semibold text-gray-600 block">Tally Server URL</span>
                <span className="text-gray-900 font-mono text-xs break-all">{company.tallyUrl || 'Not Configured'}</span>
              </div>
              <div className="bg-white p-2 rounded-md">
                <span className="font-semibold text-gray-600 block">Company GUID</span>
                <span className="text-gray-900 font-mono text-xs break-all">{company.tallyCompanyGUID || 'Not Available'}</span>
              </div>
              <div className="bg-white p-2 rounded-md">
                <span className="font-semibold text-gray-600 block">Integration Status</span>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={`w-2 h-2 rounded-full ${company.isTallyActive ? 'bg-green-500' : 'bg-red-500'}`}></span>
                  <span className="text-gray-900">{company.isTallyActive ? 'Connected & Syncing' : 'Disconnected'}</span>
                </div>
              </div>
              {company.lastTallySync && (
                <div className="bg-white p-2 rounded-md">
                  <span className="font-semibold text-gray-600 block">Last Sync</span>
                  <span className="text-gray-900">{new Date(company.lastTallySync).toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>

          {/* Domain Configuration */}
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-3 rounded-xl border border-orange-200">
            <div className="flex items-center space-x-2 mb-2">
              <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
              </svg>
              <h3 className="text-sm font-bold text-gray-800">Domain Access Configuration</h3>
              <span className="bg-orange-200 text-orange-800 text-xs px-2 py-1 rounded-full font-semibold">
                {company.domains?.length || 0} Domain{(company.domains?.length || 0) !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="space-y-2">
              {company.domains && company.domains.length > 0 ? (
                company.domains.map((domain, index) => (
                  <div key={index} className="bg-white p-2 rounded-lg shadow-sm">
                    <h4 className="font-semibold text-gray-800 mb-2 text-sm flex items-center">
                      <svg className="w-3 h-3 mr-1 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                      </svg>
                      {domain.domainName}
                    </h4>
                    <div className="grid grid-cols-1 gap-2 text-xs">
                      <CompactAccessSection 
                        label="Admin Access" 
                        values={domain.adminDashboardAccess || []} 
                        color="red"
                      />
                      <CompactAccessSection 
                        label="Company Head Access" 
                        values={domain.companyHeadDashboardAccess || []} 
                        color="yellow"
                      />
                      <CompactAccessSection 
                        label="Employee Access" 
                        values={domain.employeeDashboardAccess || []} 
                        color="green"
                      />
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-white p-3 rounded-lg text-center">
                  <span className="text-gray-500 italic text-sm">No domain configuration set up</span>
                </div>
              )}
            </div>
          </div>

          {/* Terms and Conditions */}
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-3 rounded-xl border border-yellow-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="text-sm font-bold text-gray-800">Business Terms & Conditions</h3>
              </div>
              <span className="bg-yellow-200 text-yellow-800 text-xs px-2 py-1 rounded-full font-semibold">
                {(company.quotationTermsAndConditions?.length || 0) + (company.deliveryOrderTermsAndConditions?.length || 0)} Terms
              </span>
            </div>
            <div className="grid grid-cols-1 gap-2">
              <div className="bg-white p-2 rounded-md border-l-4 border-blue-400">
                <h4 className="font-semibold text-gray-700 mb-1 text-xs flex items-center">
                  <svg className="w-3 h-3 mr-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Quotation Terms ({company.quotationTermsAndConditions?.length || 0})
                </h4>
                <div className="text-xs text-gray-600 max-h-20 overflow-y-auto">
                  {company.quotationTermsAndConditions && company.quotationTermsAndConditions.length > 0 ? (
                    <>
                      {company.quotationTermsAndConditions.slice(0, 3).map((term, index) => (
                        <div key={index} className="mb-1">• {term}</div>
                      ))}
                      {company.quotationTermsAndConditions.length > 3 && (
                        <div className="text-blue-600 font-semibold">... and {company.quotationTermsAndConditions.length - 3} more terms</div>
                      )}
                    </>
                  ) : (
                    <span className="text-gray-500 italic">No quotation terms configured</span>
                  )}
                </div>
              </div>
              
              <div className="bg-white p-2 rounded-md border-l-4 border-green-400">
                <h4 className="font-semibold text-gray-700 mb-1 text-xs flex items-center">
                  <svg className="w-3 h-3 mr-1 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 009.586 13H7" />
                  </svg>
                  Delivery Order Terms ({company.deliveryOrderTermsAndConditions?.length || 0})
                </h4>
                <div className="text-xs text-gray-600 max-h-20 overflow-y-auto">
                  {company.deliveryOrderTermsAndConditions && company.deliveryOrderTermsAndConditions.length > 0 ? (
                    <>
                      {company.deliveryOrderTermsAndConditions.slice(0, 3).map((term, index) => (
                        <div key={index} className="mb-1">• {term}</div>
                      ))}
                      {company.deliveryOrderTermsAndConditions.length > 3 && (
                        <div className="text-green-600 font-semibold">... and {company.deliveryOrderTermsAndConditions.length - 3} more terms</div>
                      )}
                    </>
                  ) : (
                    <span className="text-gray-500 italic">No delivery order terms configured</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Components for Formatting
const AccessSection = ({ label, values }) => (
  <div>
    <h5 className="font-medium text-gray-700 mb-2">{label}</h5>
    {values && values.length > 0 ? (
      <div className="flex flex-wrap gap-2">
        {values.map(access => (
          <span 
            key={access} 
            className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-md"
          >
            {access.replace(/-/g, ' ')}
          </span>
        ))}
      </div>
    ) : (
      <p className="text-sm italic text-gray-500">No access defined</p>
    )}
  </div>
);

const CompactAccessSection = ({ label, values, color = 'blue' }) => {
  const colorClasses = {
    red: 'bg-red-50 border-red-200',
    yellow: 'bg-yellow-50 border-yellow-200', 
    green: 'bg-green-50 border-green-200',
    blue: 'bg-blue-50 border-blue-200'
  };

  return (
    <div className={`p-2 rounded-md border ${colorClasses[color]}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="font-semibold text-gray-700 text-xs">{label}</span>
        <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full">
          {values?.length || 0} features
        </span>
      </div>
      {values && values.length > 0 ? (
        <div className="text-xs text-gray-600">
          {values.slice(0, 3).map(access => access.replace(/-/g, ' ')).join(', ')}
          {values.length > 3 && ` +${values.length - 3} more`}
        </div>
      ) : (
        <span className="text-xs italic text-gray-500">No access configured</span>
      )}
    </div>
  );
};

export default ViewCompanyModal;